import React from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Skeleton,
  Grid,
  Pagination,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { ForumOutlined as ForumIcon } from '@mui/icons-material';
import PageLayout from '../../components/layout/PageLayout';
import PageHeader from '../../components/layout/PageHeader';
import DirectoryFilters from '../../components/directory/DirectoryFilters';
import ProviderCard from '../../components/directory/ProviderCard';
import { Loading, ProviderGridSkeleton } from '../../components/layout/Skeletons';
import { useDirectory } from '../../hooks/directory/useDirectory';

/**
 * The services: professionals who come to you.
 *
 * Shops have their own page. They are the same kind of listing in the model —
 * a name, a category, an address, a legal identity — but not the same errand:
 * somebody looking for a plumber is choosing whom to call, somebody looking
 * for a bakery is choosing where to walk. Mixed in one list, each got in the
 * other's way.
 */
function Directory() {
  const { categorySlug } = useParams();

  const {
    filters,
    categoryOptions,
    neighborhoods,
    providers,
    meta,
    city,
    loading,
    error,
    setFilter,
    resetFilters,
  } = useDirectory({ categorySlug, kind: 'SERVICE' });

  const activeCategory = categoryOptions.find((option) => option.slug === filters.category);
  const total = meta?.total_items || 0;

  return (
    <PageLayout centered={false}>
      <Box sx={{ width: '100%' }}>
        <PageHeader
          reserveLede
          title={activeCategory ? activeCategory.label : 'Les services'}
          lede={
            city
              ? `Les artisans et les professionnels qui se déplacent chez vous à ${city.name}, recommandés par ceux qui y habitent.`
              : 'Les artisans et les professionnels qui se déplacent chez vous.'
          }
        />

        <DirectoryFilters
          filters={filters}
          categoryOptions={categoryOptions}
          neighborhoods={neighborhoods}
          onChange={setFilter}
          onReset={resetFilters}
        />

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {/* The grid is drawn before its contents so the page settles once,
            rather than growing under the reader when the answer lands. */}
        {loading && (
          <Loading label="Chargement des professionnels">
            <Skeleton variant="text" width={220} height={34} sx={{ mb: 2.5 }} />
            <ProviderGridSkeleton count={6} />
          </Loading>
        )}

        {!loading && !error && (
          <>
            {/* The count is the heading of the results; when there are none,
                the panel below says it better than a heading would. */}
            {total > 0 && (
              <Typography variant="h3" component="h2" sx={{ mb: 2.5 }}>
                {`${total} professionnel${1 === total ? '' : 's'}`}
              </Typography>
            )}

            {0 === providers.length ? (
              <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderStyle: 'dashed' }}>
                <Typography variant="h4" component="p" sx={{ mb: 1 }}>
                  Rien pour l'instant
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Essayez une autre catégorie, un autre quartier, ou effacez les filtres.
                </Typography>
                <Button onClick={resetFilters} variant="outlined">
                  Effacer la recherche
                </Button>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {providers.map((provider) => (
                  <Grid item xs={12} sm={6} md={4} key={provider.uuid} sx={{ minWidth: 0 }}>
                    <ProviderCard provider={provider} />
                  </Grid>
                ))}
              </Grid>
            )}

            {meta && meta.total_pages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                <Pagination
                  count={meta.total_pages}
                  page={meta.current_page}
                  onChange={(event, page) => setFilter('page', 1 === page ? '' : String(page))}
                />
              </Box>
            )}

            {/* The directory cannot hold every trade in the city, and a dead
                end is where somebody gives up on the site altogether. */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              sx={{
                mt: 5,
                p: { xs: 3, md: 3.5 },
                borderRadius: '18px',
                bgcolor: 'success.light',
                alignItems: { md: 'center' },
                justifyContent: 'space-between',
                gap: 3,
              }}
            >
              <Stack direction="row" sx={{ alignItems: 'center', gap: 2.25 }}>
                <ForumIcon sx={{ fontSize: 34, color: 'secondary.main', display: { xs: 'none', sm: 'block' } }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="h4" component="p" sx={{ color: 'secondary.dark' }}>
                    Vous ne trouvez pas&nbsp;?
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#52603f' }}>
                    Demandez sur le fil du quartier : vos voisins connaissent souvent quelqu'un.
                  </Typography>
                </Box>
              </Stack>
              <Button
                component={RouterLink}
                to="/quartier"
                variant="contained"
                color="secondary"
                size="large"
                sx={{ flexShrink: 0 }}
              >
                Poser ma question
              </Button>
            </Stack>
          </>
        )}
      </Box>
    </PageLayout>
  );
}

export default Directory;
